import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { adminApi } from '../api/adminApi';
import { analyticsApi } from '../api/analytics';
import axiosClient, { setAuthToken, setOnUnauthorized } from '../api/axiosClient';

type AclEntry = {
  role?: string;
  [key: string]: any;
};

let backendLoginInFlight: Promise<{ jwt: string; acl?: AclEntry[] } | null> | null = null;
let backendSessionCache: { jwt: string; acl: AclEntry[] } | null = null;

function getUserRoles(user: any, acl: AclEntry[] | null) {
  const roles = new Set<string>();
  (acl || []).forEach((a) => {
    if (a?.role) roles.add(a.role);
  });
  return roles;
}

export default function useSession() {
  const { user, isAuthenticated, isLoading, logout, getAccessTokenSilently } = useAuth0();

  const [backendToken, setBackendToken] = useState<string | null>(backendSessionCache?.jwt ?? null);
  const [acl, setAcl] = useState<AclEntry[] | null>(backendSessionCache?.acl ?? null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setOnUnauthorized(() => {
      backendSessionCache = null;
      setAuthToken(null);
      setBackendToken(null);
      setAcl(null);
      navigate('/unauthorized', { replace: true });
    });

    return () => setOnUnauthorized(null);
  }, [navigate]);

  useEffect(() => {
    const run = async () => {
      if (!isAuthenticated) return;

      if (backendSessionCache?.jwt) {
        setBackendToken(backendSessionCache.jwt);
        setAcl(backendSessionCache.acl || []);
        setAuthToken(backendSessionCache.jwt);
        return;
      }

      if (backendToken) return;

      try {
        const auth0Token = await getAccessTokenSilently();

        if (!backendLoginInFlight) {
          backendLoginInFlight = (async () => {
            const res = await axiosClient.post(
              '/admin-login',
              {},
              { headers: { Authorization: `Bearer ${auth0Token}` } } // Auth0 token ONLY here
            );
            const jwt = res.data?.jwt;
            if (!jwt) return null;
            return { jwt, acl: res.data?.acl || [] };
          })();
        }

        const session = await backendLoginInFlight;
        if (!session?.jwt) throw new Error('No backend jwt');

        backendSessionCache = {
          jwt: session.jwt,
          acl: session.acl || [],
        };
        setBackendToken(session.jwt);
        setAcl(session.acl || []);
        setAuthToken(session.jwt);
      } catch (e) {
        backendSessionCache = null;
        setAuthToken(null);
        logout({ logoutParams: { returnTo: window.location.origin } });
      } finally {
        backendLoginInFlight = null;
      }
    };

    run();
  }, [isAuthenticated, backendToken, getAccessTokenSilently, logout]);

  const handleLogout = () => {
    setIsLoggingOut(true);
    backendSessionCache = null;
    setAuthToken(null);
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const isLoggedIn = useMemo(
    () => !!user && !!backendToken && !isLoading && isAuthenticated,
    [user, backendToken, isLoading, isAuthenticated]
  );
  const rolesSet = useMemo(() => getUserRoles(user, acl), [user, acl]);
  const roles = useMemo(() => Array.from(rolesSet), [rolesSet]);

  // unified API surface
  const api = useMemo(
    () => ({
      ...adminApi,
      ...analyticsApi,
    }),
    []
  );

  return {
    user,
    backendToken,
    acl,
    isLoggedIn,
    isLoading,
    isLoggingOut,
    roles,
    rolesSet,
    handleLogout,
    api,
  } as const;
}
